import { ErrorBoundary } from "react-error-boundary";
import { Button } from "@/components/ui/button";
import { AlertOctagon, RefreshCw, Home } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Page-level error boundary fallback. Keeps the sidebar / layout intact and
 * just renders a friendly message in place of the crashed route content.
 * The "Try again" button calls `resetErrorBoundary()` which re-mounts the
 * subtree — usually enough to recover from a transient render error.
 */
function PageErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="page-error-fallback">
      <div className="max-w-md w-full border border-red-200 bg-red-50/40 rounded-sm p-6 sm:p-8">
        <div className="flex items-center gap-2.5 mb-3">
          <AlertOctagon size={20} className="text-red-600 shrink-0" />
          <h2 className="text-lg font-semibold text-[#0a2540]" style={{ fontFamily: "Outfit" }}>
            Something went wrong on this page
          </h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          We hit an unexpected error rendering this view. The rest of the portal is still working — head back to the
          dashboard, or try reloading this page.
        </p>
        {error?.message && (
          <details className="mb-5 text-[11px] font-mono-data text-slate-500 bg-white border border-slate-200 rounded-sm px-3 py-2">
            <summary className="cursor-pointer text-slate-600">Error details</summary>
            <div className="mt-2 break-all whitespace-pre-wrap">{error.message}</div>
          </details>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={resetErrorBoundary}
            className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm"
            data-testid="btn-error-retry"
          >
            <RefreshCw size={14} className="mr-1.5" /> Try again
          </Button>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 hover:border-[#0a2540] hover:text-[#0a2540] rounded-sm text-sm text-slate-600"
            data-testid="link-error-home"
          >
            <Home size={14} /> Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function PageErrorBoundary({ children, routeKey }) {
  return (
    <ErrorBoundary
      FallbackComponent={PageErrorFallback}
      // Re-mount the boundary's subtree whenever the route changes — otherwise a
      // crashed page stays "stuck" on the fallback while the user navigates away.
      resetKeys={[routeKey]}
      onError={(error, info) => {
        console.error("PageErrorBoundary caught:", error, info?.componentStack);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
